import React from 'react'
import styled from 'styled-components'
import { Button } from '@streamr/streamr-layout'
import { TABLET, DESKTOP, REGULAR } from '@streamr/streamr-layout/styled'
import Container from '~shared/Container'
import Link from '~shared/Link'
import Spacer from '~shared/Spacer'
import Display from '~shared/Display'
import VisualizerNav from '~shared/VisualizerNav'
import Press from '~shared/Press'
import ScrollEncourager from '~shared/ScrollEncourager'
import Br from '~shared/Br'
import ButtonWrapper from '~shared/ButtonWrapper'
import urls from '~utils/urls'

const Inner = styled.div`
    margin: 0 auto;
    max-width: 312px;
    padding-bottom: 140px;

    ${Button} {
        display: none;
    }

    /* handle custom breakpoint for Press section */
    @media (min-width: 348px) {
        padding-bottom: 110px;
    }

    @media (min-width: ${TABLET}px) {
        max-width: 520px;
        padding-bottom: 0;
    }

    @media (min-width: ${DESKTOP}px) {
        padding-left: 50%;
        padding: 152px 0 0 50%;
        width: 50%;

        ${Button} {
            display: inline-flex;
        }
    }
`

const ContentArea = styled.div`
    h1 {
        font-size: 28px;
        font-weight: ${REGULAR};
        line-height: 36px;
        margin: 0;
    }

    p {
        font-size: 18px;
        line-height: 26px;
    }

    @media (min-width: ${TABLET}px) {
        h1 {
            font-size: 40px;
            line-height: 52px;
        }

        p {
            font-size: 24px;
            line-height: 34px;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        h1 {
            font-size: 48px;
            line-height: 56px;
        }
    }
`

const Outer = styled.div`
    align-items: flex-end;
    display: flex;
    flex-direction: row;
    height: 100%;
`

const PressWrapper = styled.div`
    box-sizing: border-box;
    bottom: 0;
    left: 0;
    padding: 0 16px;
    position: absolute;
    width: 100%;

    ${Press} {
        transform: translateY(50%) translateY(-17px);
    }

    @media (min-width: ${TABLET}px) {
        padding: 0;
    }
`

const Wrapper = styled.div`
    width: 100%;
`

const UnstyledHero = ({ className }) => (
    <div className={className}>
        <VisualizerNav
            offsetFn={(canvasWidth) => {
                let [x, y] = [0, 0]

                if (canvasWidth > 1182) {
                    [x, y] = [0.2, 0]
                } else if (canvasWidth < 1183 && canvasWidth > 751) {
                    [x, y] = [0.0, 0.2]
                } else {
                    [x, y] = [0.0, 0.1]
                }

                return {
                    x,
                    y,
                }
            }}
        />
        <Container>
            <Outer>
                <Wrapper>
                    <Inner>
                        <ContentArea>
                            <h1 $appear>
                                Streamr.
                                <br />
                                {/* &#x2011; is the unbreakable dash. */}
                                The&nbsp;decentralized
                                {' '}
                                <br />
                                {' '}
                                real&#x2011;time data network
                            </h1>
                            <Spacer
                                $appear
                                as="p"
                                head="32px"
                                tail="0,0,32px"
                            >
                                Create, share
                                {' '}
                                <Display as="span" mobile="none" tablet="inline" desktop="inline">and</Display>
                                <Display as="span" tablet="none" desktop="none">&amp;</Display>
                                {' '}
                                consume data streams
                                <Br tablet desktop />
                                on an open, scalable P2P protocol
                                <Br tablet desktop />
                                with powerful tools for monetization
                            </Spacer>
                            <ButtonWrapper>
                                <Button
                                    tag={Link}
                                    size="big"
                                    kind="primary"
                                    href={urls.apps.networkExplorer}
                                    newTab
                                    noDecoration
                                    noHover
                                >
                                    Explore the Network
                                </Button>
                            </ButtonWrapper>
                            <ButtonWrapper>
                                <Button
                                    tag={Link}
                                    size="big"
                                    kind="primary"
                                    outline
                                    href="https://medium.com/streamrblog/how-to-join-the-brubeck-testnet-c8b823c847f8"
                                    newTab
                                    noDecoration
                                    noHover
                                >
                                    Join the testnet
                                </Button>
                            </ButtonWrapper>
                            <Display as={ScrollEncourager} offset={60} desktop="none" />
                        </ContentArea>
                    </Inner>
                    <Display as={ScrollEncourager} offset={80} center mobile="none" desktop="block" />
                </Wrapper>
            </Outer>
            <PressWrapper>
                <Press />
            </PressWrapper>
        </Container>
    </div>
)

const Hero = styled(UnstyledHero)`
    height: 100vh;
    position: relative;

    ${VisualizerNav} {
        color: #0c009a;
        left: 0;
        position: absolute;
        top: 0;
        z-index: 0;
    }

    ${Container} {
        height: 100%;
        pointer-events: none;
        position: relative;
        z-index: 1;
    }

    ${Press},
    ${ScrollEncourager},
    ${ContentArea} {
        pointer-events: auto;
    }

    ${ButtonWrapper} + ${ButtonWrapper} {
        margin-left: 32px;
    }

    ${ScrollEncourager} {
        margin-top: 32px;
    }

    @media (min-width: ${TABLET}px) {
        ${ScrollEncourager} {
            margin-top: 48px;
            margin-bottom: 100px;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        height: 1030px;

        ${ScrollEncourager} {
            margin-top: 140px;
            margin-bottom: 140px;
        }
    }
`

export default Hero
